// OrgCodeDialog.tsx — lets a signed-in FREE member enter an organisation or gift code
// from Settings, if they missed it (or were given one) after sign-up. Reuses
// auth.redeemOrgCode, which refreshes entitlement on success, so the app unlocks at
// once. House style: no emoji, no em-dash.
import React from 'react';
import { Display, Body, Btn } from '@/components/ui';
import { useAuth } from '@/hooks/useAuth';

export function OrgCodeDialog({ onClose }: { onClose: () => void }) {
  const auth = useAuth();
  const [code, setCode] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const [done, setDone] = React.useState<{ orgName?: string; termMonths?: number } | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const ready = code.trim().length >= 3 && !busy;

  const onRedeem = async () => {
    if (!ready) return;
    setBusy(true);
    setError(null);
    const res = await auth.redeemOrgCode(code.trim());
    setBusy(false);
    if (res.granted) {
      setDone({ orgName: res.orgName, termMonths: res.termMonths });
      return;
    }
    setError(
      res.reason === 'seats_full'
        ? 'All of this organisation’s places are currently in use. Please check with whoever shared the code.'
        : res.reason === 'expired'
          ? 'This organisation’s access has expired.'
          : res.reason === 'cancelled'
            ? 'This organisation’s access is no longer active.'
            : res.error || 'That code was not recognised. Please check it and try again.',
    );
  };

  return (
    <div role="dialog" aria-modal="true" aria-label="Enter a code" style={overlay}>
      <div style={panel}>
        {done ? (
          <>
            <Display text="You’re in" size={26} weight={400} style={{ marginBottom: 12 }} />
            <Body size={14.5} style={{ marginBottom: 22, lineHeight: 1.6 }}>
              {done.orgName ? `Your access is now provided by ${done.orgName}.` : 'Your full access has been unlocked.'}
              {done.termMonths ? ` You have the full programme for ${done.termMonths} months.` : ''}
            </Body>
            <Btn full size="lg" onClick={onClose}>Continue</Btn>
          </>
        ) : (
          <>
            <Display text="Enter your code" size={26} weight={400} style={{ marginBottom: 12 }} />
            <Body size={14.5} style={{ marginBottom: 14, lineHeight: 1.6 }}>
              If your organisation, or someone else, has given you a code or a gift of access, enter it here to unlock the full programme.
            </Body>

            <Body size={13} style={{ margin: '0 0 6px' }}>Organisation or gift code</Body>
            <input
              type="text"
              autoCapitalize="characters"
              autoCorrect="off"
              autoComplete="off"
              spellCheck={false}
              value={code}
              onChange={(e) => { setCode(e.target.value.toUpperCase()); setError(null); }}
              placeholder="RN-XXXXXX-XXXX"
              aria-label="Organisation or gift code"
              style={{ ...field, letterSpacing: '0.04em' }}
            />

            {error && (
              <Body role="alert" size={13} style={{ marginTop: 10, color: '#8B2F1A', lineHeight: 1.5 }}>{error}</Body>
            )}

            <div style={{ display: 'flex', gap: 10, marginTop: 22 }}>
              <Btn full variant="ghost" size="lg" onClick={onClose} disabled={busy}>Cancel</Btn>
              <Btn full size="lg" onClick={() => void onRedeem()} disabled={!ready} ariaLabel="Redeem code">
                {busy ? 'Checking…' : 'Unlock access'}
              </Btn>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

const overlay: React.CSSProperties = {
  position: 'fixed',
  inset: 0,
  zIndex: 1100,
  background: 'rgba(20,16,12,0.55)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '24px',
  paddingTop: 'calc(var(--safe-top) + 24px)',
  paddingBottom: 'calc(var(--safe-bottom) + 24px)',
};
const panel: React.CSSProperties = {
  width: '100%',
  maxWidth: 440,
  background: 'var(--card-top, #fff)',
  border: '1px solid var(--hair-strong, rgba(0,0,0,0.1))',
  borderRadius: 20,
  padding: 24,
  textAlign: 'left',
  maxHeight: '90vh',
  overflowY: 'auto',
};
const field: React.CSSProperties = {
  width: '100%',
  boxSizing: 'border-box',
  padding: '13px 14px',
  borderRadius: 13,
  border: '1px solid var(--hair-strong, rgba(0,0,0,0.18))',
  fontFamily: 'Inter, system-ui, sans-serif',
  fontSize: 15,
  color: 'var(--ink)',
  background: 'var(--card-bot, #fff)',
};
